import { Badge } from '../ui/Badge';
import { Bell, BellRing } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useUnacknowledgedAlertCount } from '../../hooks/useFinancialSecurity';

interface AlertBadgeProps {
  count?: number;
  showIcon?: boolean;
  className?: string;
}

export function AlertBadge({ count: propCount, showIcon = true, className }: AlertBadgeProps) {
  const hookCount = useUnacknowledgedAlertCount();
  const count = propCount ?? hookCount;

  if (count === 0) {
    return showIcon ? (
      <Bell className={cn('h-5 w-5 text-muted-foreground', className)} />
    ) : null;
  }

  return (
    <div className={cn('relative inline-flex', className)}>
      {showIcon && (
        <BellRing className="h-5 w-5 text-orange-500 animate-pulse" />
      )}
      <Badge
        variant="destructive"
        className={cn(
          'absolute -top-2 -right-2 h-5 min-w-5 flex items-center justify-center p-0 text-xs',
          !showIcon && 'relative top-0 right-0'
        )}
      >
        {count > 99 ? '99+' : count}
      </Badge>
    </div>
  );
}

interface AlertNotificationProps {
  title: string;
  message: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  time: string;
  onAcknowledge?: () => void;
  className?: string;
}

export function AlertNotification({
  title,
  message,
  severity,
  time,
  onAcknowledge,
  className,
}: AlertNotificationProps) {
  const severityConfig = {
    INFO: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
    },
    WARNING: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
    },
    CRITICAL: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
    },
  };

  const config = severityConfig[severity];

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        config.bgColor,
        config.borderColor,
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className={cn('font-medium', config.textColor)}>{title}</p>
          <p className="text-sm text-muted-foreground mt-1">{message}</p>
          <p className="text-xs text-muted-foreground mt-2">{time}</p>
        </div>
        {onAcknowledge && (
          <button
            onClick={onAcknowledge}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Reconhecer
          </button>
        )}
      </div>
    </div>
  );
}

export default AlertBadge;
